// src/macroSplits.ts
// Macro-strefy do stawek: NIE wpływają na klikanie ani na geojsony.
// Z istniejącego klikniętego poligonu robimy kod "macro" (np. PL-N / PL-E / PL-S / PL-W).

type LngLat = [number, number];

function centroidOfGeom(geom: any): LngLat {
  const coords: LngLat[] = [];

  function collect(v: any) {
    if (!v) return;
    if (typeof v[0] === "number" && typeof v[1] === "number") coords.push([v[0], v[1]]);
    else if (Array.isArray(v)) for (const x of v) collect(x);
  }

  collect(geom.coordinates);

  if (!coords.length) return [0, 0];

  const lon = coords.reduce((s, v) => s + v[0], 0) / coords.length;
  const lat = coords.reduce((s, v) => s + v[1], 0) / coords.length;
  return [lon, lat];
}

// 4-trójkąty jak na Twoich zdjęciach (X): N/E/S/W wg kąta od środka kraju
function wedgeNESWFromCenter(center: LngLat, p: LngLat): "N" | "E" | "S" | "W" {
  const dx = p[0] - center[0]; // lon
  const dy = p[1] - center[1]; // lat

  // kąt w stopniach: 0=E, 90=N, 180=W, 270=S
  const ang = (Math.atan2(dy, dx) * 180) / Math.PI;
  const a = (ang + 360) % 360;

  // Granice na przekątnych (linie po skosie): 45/135/225/315
  // E: -45..45, N: 45..135, W: 135..225, S: 225..315
  if (a >= 45 && a < 135) return "N";
  if (a >= 135 && a < 225) return "W";
  if (a >= 225 && a < 315) return "S";
  return "E";
}

// Linia podziału (strona A/B) po prostej przez 2 punkty
function sideOfLine(a: LngLat, b: LngLat, p: LngLat): number {
  // (b-a) x (p-a) (iloczyn wektorowy w 2D)
  return (b[0] - a[0]) * (p[1] - a[1]) - (b[1] - a[1]) * (p[0] - a[0]);
}

const UNSPLIT_COUNTRIES = new Set<string>([
  // tu wrzuć wszystkie kraje, które mają być “całością” do stawek
  "NL", "BE", "LU", "DK", "AT", "CZ", "SK", "PT", "RO", "BG", "HR", "GR",
  "LT", "LV", "EE", "FI", "IE",
  "CH", // jeśli chcesz mieć całą CH jedną stawką (na razie)
  "AL", "BA", "RS", "XK", "MD", "ME", "MC", "LI"
]);

// Środki krajów do podziału na 4 (N/E/S/W) – ustawione “w przybliżeniu”, ale stabilne.
// Nie musisz mieć geojsonu kraju – wystarcza centroid klikniętego poligonu.
const CENTERS_4WAY: Record<string, LngLat> = {
  PL: [19.145, 52.10],
  DE: [10.45, 51.15],
  FR: [2.20, 46.40],
  ES: [-3.70, 40.20],
};

// IT: północ/południe “do wysokości Rzymu”
const IT_SPLIT_LAT_ROME = 41.90;

// UK: południe “do wysokości Manchesteru”
const UK_SPLIT_LAT_MANCHESTER = 53.48;

// HU: północ/południe (orientacyjnie: okolice Budapesztu)
const HU_SPLIT_LAT_BUDAPEST = 47.30;

// Skandynawia: linia “jak na Twoim rysunku” (skośna przez NO/SE)
// Daje sensowny podział N/S w praktyce.
const SCANDI_LINE_A: LngLat = [5.0, 62.6];   // zachód Norwegii
const SCANDI_LINE_B: LngLat = [20.0, 59.9];  // okolice Sztokholmu / Bałtyk

export function toMacroZoneId(zoneId: string, geom: any): string {
  if (!zoneId) return zoneId;

  const cc = zoneId.slice(0, 2).toUpperCase();
  const p = centroidOfGeom(geom);

  // kraje “całe”
  if (UNSPLIT_COUNTRIES.has(cc)) return cc;

  // 4-way: PL/DE/FR/ES (N/E/S/W po przekątnych)
  if (CENTERS_4WAY[cc]) {
    const c = CENTERS_4WAY[cc];
    const w = wedgeNESWFromCenter(c, p);
    return `${cc}-${w}`;
  }

  // HU północ/południe (potrzebne do stawek HU-N / HU-S)
  if (cc === "HU") {
    const lat = p[1];
    return lat >= HU_SPLIT_LAT_BUDAPEST ? "HU-N" : "HU-S";
  }

  // IT północ/południe
  if (cc === "IT") {
    const lat = p[1];
    return lat >= IT_SPLIT_LAT_ROME ? "IT-N" : "IT-S";
  }

  // UK północ/południe
  if (cc === "GB" || cc === "UK") {
    const lat = p[1];
    return lat >= UK_SPLIT_LAT_MANCHESTER ? "GB-N" : "GB-S";
  }

  // NO / SE północ/południe po linii skośnej
  if (cc === "NO" || cc === "SE") {
    const s = sideOfLine(SCANDI_LINE_A, SCANDI_LINE_B, p);
    // jedna strona = północ, druga = południe (odwrócisz jednym znakiem jeśli Ci się nie spodoba)
    const part = s >= 0 ? "N" : "S";
    return `${cc}-${part}`;
  }

  // fallback: jak nie znamy kraju, nie psujemy nic
  return zoneId.toUpperCase();
}
